import Constants from 'src/core/utils/Constants';
import { KeyboardAvoidingView, Modal, Text, View, StyleSheet } from 'react-native';
import { HelperText, TextInput } from 'react-native-paper';
import Translations from 'src/core/utils/Translations';
import { PrimaryButton, SecondaryButton } from 'src/presentation/components/molecules/Button';
import dialogStyles from 'src/presentation/dialogs/styles/DialogStyles';
import SelectField, { type PickerItem } from 'src/presentation/components/molecules/SelectField';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import { useWorkspacesStore } from 'src/domain/stores/useWorkspacesStore';
import { useEffect, useState } from 'react';
import { useReservationsStore } from 'src/domain/stores/useReservationsStore';

interface Props {
	onCancelled: () => void;
	onSuccess: () => void;
	isDialogVisible?: boolean;
}

const AddReservationManuallyDialog = ({ onCancelled, onSuccess, isDialogVisible }: Props) => {
	const { workspaces } = useWorkspacesStore();
	const {
		isAddingReservation,
		addReservationErrorMessage,
		postApiAddReservationManually,
		setAddReservationErrorMessage,
	} = useReservationsStore();

	const [reservationNumber, setReservationNumber] = useState<string | null>(null);
	const [selectedWorkspace, setSelectedWorkspace] = useState<string>(Constants.workspacesDefaultItemValue);

	useEffect(() => {
		setSelectedWorkspace(Constants.workspacesDefaultItemValue);
		setReservationNumber(null);
		setAddReservationErrorMessage(null);
	}, [isDialogVisible, setAddReservationErrorMessage]);

	const getWorkspaceItems: () => Array<PickerItem> = () => {
		const mappedWorkspaces = workspaces.map((w) => {
			return { label: w.title, value: w.id.toString() };
		});

		const defaultItem = {
			label: Translations.selectAThermenResort,
			value: Constants.workspacesDefaultItemValue,
		};
		return [defaultItem, ...mappedWorkspaces];
	};

	const onPressAdd = async () => {
		if (selectedWorkspace === Constants.workspacesDefaultItemValue || !reservationNumber) {
			setAddReservationErrorMessage(Translations.fillInAllRequiredFields);
			return;
		}

		await postApiAddReservationManually({
			workspaceId: selectedWorkspace,
			reservationNumber,
			onSuccess: () => onSuccess(),
		});
	};

	return (
		<Modal animationType="fade" style={dialogStyles.modalView} transparent={true} visible={isDialogVisible}>
			<KeyboardAvoidingView behavior="padding" style={dialogStyles.centeredView}>
				<View style={dialogStyles.modalCard}>
					<Text style={dialogStyles.modalTitle}>{Translations.addReservationManually}</Text>

					<View style={styles.divider} />

					<SelectField
						items={getWorkspaceItems()}
						style={styles.workspacesSelectFieldStyle}
						value={selectedWorkspace}
						onValueChange={setSelectedWorkspace}
					/>

					<TextInput
						activeUnderlineColor={Colors.green}
						label={Translations.reservationNumber}
						selectionColor={Colors.gray600}
						style={styles.reservationNumberInputyStyle}
						value={reservationNumber ?? ''}
						onChangeText={setReservationNumber}
					/>

					{addReservationErrorMessage && (
						<HelperText style={styles.errorMessage} type="error">
							{addReservationErrorMessage}
						</HelperText>
					)}

					<View style={{ flexDirection: 'row' }}>
						<SecondaryButton extraStyle={styles.secondaryButtonStyle} label={Translations.cancel} onPress={onCancelled} />
						<PrimaryButton
							extraStyle={styles.primaryButtonStyle}
							label={Translations.add}
							loading={isAddingReservation}
							onPress={onPressAdd}
						/>
					</View>
				</View>
			</KeyboardAvoidingView>
		</Modal>
	);
};

const styles = StyleSheet.create({
	divider: {
		width: '100%',
		height: Dimens.size1,
		marginBottom: Dimens.padding20,
	},
	workspacesSelectFieldStyle: {
		marginBottom: Dimens.padding12,
		marginTop: Dimens.paddingMinus2,
	},
	reservationNumberInputyStyle: {
		backgroundColor: Colors.gray200,
		marginBottom: Dimens.padding20,
	},
	secondaryButtonStyle: {
		paddingHorizontal: Dimens.padding10,
		flex: Dimens.flex1,
	},
	primaryButtonStyle: {
		paddingHorizontal: Dimens.padding10,
		flex: Dimens.flex1,
	},
	errorMessage: {
		marginBottom: Dimens.padding20,
	},
});

export default AddReservationManuallyDialog;
